/**
 * FoodPulseAuthGate
 * ------------------
 * Mounts once for the whole /food-pulse subtree. On every auth change:
 *   1. Fetches the user's FoodPulse profile bundle (GET /foodpulse/profile/me).
 *   2. If `foodpulse_profile_complete` is false AND the current route
 *      is NOT already the onboarding or business-signup page, redirects
 *      to /food-pulse/profile/onboarding.
 *   3. Otherwise, if completion_pct < 60 and the user hasn't dismissed
 *      the nag today, shows a small yellow banner with a link to finish.
 *
 * The nag banner respects `sessionStorage["fp_nag_dismissed"]` so it
 * doesn't reappear until the tab is closed.
 */
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { X, ArrowRight } from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const PURPLE = "#7C3AED";
const CREAM = "#FBF7EF";

const SKIP_PATHS = [
  "/food-pulse/profile/onboarding",
  "/food-pulse/business/",   // any /business/* route — signup, edit, claim — bypasses personal gate
  "/food-pulse/claim",
];

export default function FoodPulseAuthGate() {
  const { user, loading: authLoading } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [pct, setPct] = useState(null);
  const [complete, setComplete] = useState(true);
  const [dismissed, setDismissed] = useState(sessionStorage.getItem("fp_nag_dismissed") === "1");

  useEffect(() => {
    if (authLoading || !user) { setPct(null); return; }
    // Only fire the gate on FoodPulse routes to avoid touching SocialPulse.
    if (!loc.pathname.startsWith("/food-pulse")) return;
    http.get("/foodpulse/profile/me").then((r) => {
      const p = r.data;
      setPct(p.completion_pct);
      setComplete(p.profile.foodpulse_profile_complete);
      if (!p.profile.foodpulse_profile_complete && !SKIP_PATHS.some((s) => loc.pathname.startsWith(s))) {
        nav("/food-pulse/profile/onboarding", { replace: true });
      }
    }).catch(() => { /* keep app usable if endpoint is down */ });
  }, [user, authLoading, loc.pathname, nav]);

  if (complete) return null;
  if (pct == null) return null;
  if (pct >= 60) return null;
  if (dismissed) return null;
  if (SKIP_PATHS.some((s) => loc.pathname.startsWith(s))) return null;

  return (
    <div className="fixed bottom-3 left-3 right-3 md:left-auto md:right-4 md:w-[380px] rounded-2xl shadow-lg z-50 p-3 flex items-start gap-3" style={{ background: CREAM, border: `2px solid ${PURPLE}` }} data-testid="fp-nag-banner">
      <div className="flex-1">
        <div className="text-sm font-bold" style={{ color: PURPLE }}>Finish your FoodPulse profile — {pct}% done</div>
        <div className="text-xs text-gray-700 mt-0.5">The more you share, the sharper your deals, loyalty rewards, and rescue alerts.</div>
        <Link to="/food-pulse/profile/onboarding" className="inline-flex items-center gap-1 mt-1 text-xs font-bold" style={{ color: PURPLE }} data-testid="fp-nag-cta">
          Complete now <ArrowRight size={12} />
        </Link>
      </div>
      <button onClick={() => { sessionStorage.setItem("fp_nag_dismissed", "1"); setDismissed(true); }} className="text-gray-500" data-testid="fp-nag-dismiss"><X size={16} /></button>
    </div>
  );
}
